import type { ISearchTypeConfig, TSearchType } from './types';

export const FIND_VIEW = 'find';

export const FIND_ROUTE_PATHS = {
  findUser: '/users/find',
  userDetail: '/users/find/$userId',
};

export const FIND_API_PATHS = {
  searchUsers: '/v1/api/platform/users',
  searchUsersByName: '/v1/api/platform/users/search/:name',
  fetchDocumentType: '/v1/api/platform/users/document-types',
  wallets: '/v1/api/platform/users/wallets/:userId/:country',
  onboarding: '/v1/api/platform/users/onboarding/information/:userId',
  coins: '/v1/api/platform/users/coins/:userId',
  actions: '/v1/api/platform/users/execute/user/actions',
  debitCards: '/v1/api/platform/users/debit-card/search',
  debitCardDetail: '/v1/api/platform/users/debit-card/detail/:cardId',
  creditCards: '/v1/api/platform/users/credit-cards/:userId',
  kycDocuments: '/v1/api/platform/users/identity-validation/processed/:userId',
  identityValidationLogs: '/v1/api/platform/kyc/users/:userId/identity-validation/logs',
  documentFile: '/v1/api/platform/users/documents/file',
  restrictions: '/v1/api/platform/users/restrictions/:userId',
  restrictionsSplit: '/v1/api/platform/users/restrictions/:userId/split',
  userLogs: '/v1/api/platform/users/logs/:userId',
  userActions: '/v1/api/platform/users/actions',
  adminLogs: '/v1/api/platform/users/app/logs/:userId',
  signInLogs: '/v1/api/platform/users/sign-in/logs/:userId',
  onboardingLogs: '/v1/api/platform/users/onboarding/logs/:userId',
  userAttributes: '/v1/api/platform/users/attributes/:userId',
  availableFlags: '/v1/api/platform/users/flags',
  loyaltyFiles: '/v1/api/platform/users/loyalty/:userId',
  loyaltyFileUpdate: '/v1/api/platform/users/loyalty/files/:fileId',
  documentFileBase64: '/v1/api/platform/users/documents/file/base64',

  // ── KYC document ─────────────────────────────────────────────────────────────
  updateKycDocument: '/v1/api/platform/kyc/users/:userId/document',

  // ── User actions ────────────────────────────────────────────────────────────
  markUserPep: '/v1/api/platform/users/mark-user-pep/:userId',
  loginUnlock: '/v1/api/platform/users/login/unlock/:userId',
  reprocessKyc: '/v1/api/platform/users/kyc/reprocess/:userId',
  blockTransfer: '/v1/api/platform/users/transfer/block/:userId',
  executeUserAction: '/v1/api/platform/users/execute-user-action',

  // ── Card actions ─────────────────────────────────────────────────────────────
  debitCardToggleLock: '/v1/api/platform/users/debit-card/toggleLockCs',
  debitCardCancel: '/v1/api/platform/users/debit-card/cancel',
  creditCardCancel: '/v1/api/platform/users/credit-cards/:cardId',

  // ── Restriction actions ───────────────────────────────────────────────────────
  deleteRestriction: '/v1/api/platform/users/restrictions/:restrictionId',
  revokeRestriction: '/v1/api/platform/users/restrictions/history/:restrictionId',

  // ── Statement ────────────────────────────────────────────────────────────────
  walletStatement: '/v1/api/platform/wallets/:walletId/statement',

  // ── Download document ─────────────────────────────────────────────────────
  downloadDocument: '/v1/api/platform/users/:userId/document',

  // ── Recover deleted user data ─────────────────────────────────────────────
  recoverDeletedUser: '/v1/api/platform/users/:userId/deleted/decrypt',

  // ── High risk / fraud flags ───────────────────────────────────────────────────
  userFraudFlags: '/v1/api/platform/fraud/users/:userId/flags',
  updateUserFraudFlag: '/v1/api/platform/fraud/users/:userId/flags/:userFlagId',

  // ── Features ──────────────────────────────────────────────────────────────────
  userFeatures: '/v1/api/platform/users/features',
  featuresStatus: '/v1/api/platform/users/features-status',
  changeFeatureStatus: '/v1/api/platform/users/change-status-feature',
  addCountryFeature: '/v1/api/platform/users/add-country-feature',

  // ── Special types ─────────────────────────────────────────────────────────────
  specialTypesByUser: '/v1/api/platform/users/special-types/:userId',
  specialTypesAll: '/v1/api/platform/users/special-types/types',
  specialTypesCreate: '/v1/api/platform/users/special-types/create',
  specialTypesDelete: '/v1/api/platform/users/special-types/delete/:userSpecialTypeId',

  // ── Partners ─────────────────────────────────────────────────────────────────
  partnersUserInfo: '/v1/api/platform/partners/:userId',
};

/** Partner id that identifies the AstroPay platform itself. */
export const ASTROPAY_PARTNER_ID = 999;

/**
 * Query Keys for React Query
 * Hierarchical structure for cache management
 */
export const UserSearchKeys = {
  all: () => ['users', 'search'] as const,
  simple: (searchType: string, searchTerm: string) =>
    [...UserSearchKeys.all(), 'simple', searchType, searchTerm] as const,
  document: (documentType: string, documentNumber: string) =>
    [...UserSearchKeys.all(), 'document', documentType, documentNumber] as const,
  lastName: (lastName: string, country: string, birthDate?: string, page?: number) =>
    [...UserSearchKeys.all(), 'lastName', lastName, country, birthDate, page] as const,
  documentTypes: () => ['users', 'document-types'] as const,
  detail: (userId: string) => [...UserSearchKeys.all(), 'detail', userId] as const,
  kycDocuments: (userId: string) => [...UserSearchKeys.all(), 'kyc', userId] as const,
  identityValidationLogs: (userId: string) =>
    [...UserSearchKeys.all(), 'identity-validation-logs', userId] as const,
  partnerInfo: (userId: string) =>
    [...UserSearchKeys.all(), 'partner-info', userId] as const,
};

// Search type configurations (i18n keys)
export const SEARCH_TYPE_CONFIGS: Record<TSearchType, ISearchTypeConfig> = {
  userId: {
    id: 'userId',
    labelKey: 'pages.users.find.searchTypes.userId.label',
    placeholderKey: 'pages.users.find.searchTypes.userId.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.userId.description',
    isComplex: false,
  },
  externalId: {
    id: 'externalId',
    labelKey: 'pages.users.find.searchTypes.externalId.label',
    placeholderKey: 'pages.users.find.searchTypes.externalId.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.externalId.description',
    isComplex: false,
  },
  mobile: {
    id: 'mobile',
    labelKey: 'pages.users.find.searchTypes.mobile.label',
    placeholderKey: 'pages.users.find.searchTypes.mobile.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.mobile.description',
    isComplex: false,
  },
  email: {
    id: 'email',
    labelKey: 'pages.users.find.searchTypes.email.label',
    placeholderKey: 'pages.users.find.searchTypes.email.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.email.description',
    isComplex: false,
  },
  document: {
    id: 'document',
    labelKey: 'pages.users.find.searchTypes.document.label',
    placeholderKey: 'pages.users.find.searchTypes.document.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.document.description',
    isComplex: true,
  },
  lastName: {
    id: 'lastName',
    labelKey: 'pages.users.find.searchTypes.lastName.label',
    placeholderKey: 'pages.users.find.searchTypes.lastName.placeholder',
    descriptionKey: 'pages.users.find.searchTypes.lastName.description',
    isComplex: true,
  },
};
